/**
 * Provider-agnostic view of a video join payload.
 *
 * Two backend endpoints hand out room credentials in the same shape — the
 * live-class join (GET /live-classes/:id/join, JoinLiveClassPayload) and the
 * 1-1 consultation join (GET /bookings/:id/call/join, JoinConsultationPayload).
 * Which SDK a room runs on is the ADMIN's choice (General Settings → Video
 * Providers), locked onto the class / booking the first time its room opens;
 * the page never decides — it resolves the payload through here and mounts the
 * matching room (<MeetingProvider> for VideoSDK, <LiveKitRoom> for LiveKit).
 *
 * Both payloads always carry the original VideoSDK keys (meeting_id + token +
 * user_name + role) plus `provider`, `is_host`, `can_publish` and, for LiveKit,
 * the connection details its client needs (server_url, room_name, identity).
 */

export type VideoProvider = "videosdk" | "livekit";

export interface VideoSession {
  provider: VideoProvider;
  /** VideoSDK meetingId / LiveKit room name. */
  meetingId: string;
  /** The SDK's own JWT (VideoSDK auth token / LiveKit access token). */
  token: string;
  /** Server-signed: class instructor / consultation astrologer (or an admin). */
  isHost: boolean;
  /** The payload's own role word ("host"|"participant", "astrologer"|"seeker"). */
  role: string;
  canPublish: boolean;
  userName: string | null;
  /** VideoSDK: the participantId pinned inside the token. */
  participantId?: string | null;
  /** LiveKit: wss:// URL, room name, and this participant's identity. */
  serverUrl?: string;
  roomName?: string;
  identity?: string | null;
  /** Consultations: the other party's user id (socket signalling). */
  counterpartUserId?: number | null;
  /** Consultations: phone bookings keep the camera off. */
  audioOnly?: boolean;
  durationMin?: number | null;
  /** Consultations: "YYYY-MM-DD HH:mm:ss" (UTC) or ISO, once the call went live. */
  meetingStartedAt?: string | null;
  expiresAt?: string | null;
  /** The payload as received, for anything not modelled above. */
  raw: Record<string, unknown>;
}

export type VideoSessionError = "missing" | "malformed" | "unsupported";
export type VideoSessionResult =
  | { ok: true; session: VideoSession }
  | { ok: false; error: VideoSessionError };

/** Both providers' tokens are JWTs (header.payload.signature, base64url). A
 * quick shape check catches the wrong field coming back before it is handed to
 * either SDK, which would only report a generic 4xx. */
export const isJwtShaped = (value: unknown): boolean =>
  /^[\w-]+\.[\w-]+\.[\w-]+$/.test(String(value ?? ""));

/** The JWT's claims, or null when it can't be read. */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    if (typeof atob !== "function") return null;
    const part = token.split(".")[1] ?? "";
    const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** The join payload itself, whether the caller passed the whole envelope
 * ({ success, message, data }) or already its `data`. */
function unwrapPayload(response: unknown): Record<string, unknown> {
  const outer = (response ?? {}) as Record<string, unknown>;
  const inner = outer.data;
  if (inner && typeof inner === "object" && ("token" in (inner as object) || "meeting_id" in (inner as object))) {
    return inner as Record<string, unknown>;
  }
  return outer;
}

const str = (value: unknown): string | null =>
  value === null || value === undefined || value === "" ? null : String(value);

export function resolveVideoSession(
  response: unknown,
  user?: { id?: number | string | null; name?: string | null } | null,
): VideoSessionResult {
  const data = unwrapPayload(response);
  const provider = (data.provider ?? "videosdk") as string;
  if (provider !== "videosdk" && provider !== "livekit") return { ok: false, error: "unsupported" };

  const meetingId = str(data.meeting_id);
  const token = str(data.token);
  if (!meetingId || !token) return { ok: false, error: "missing" };
  if (!isJwtShaped(token)) return { ok: false, error: "malformed" };

  const role = str(data.role) ?? "";
  const isHost =
    typeof data.is_host === "boolean"
      ? data.is_host
      : role === "host" || role === "astrologer";
  const durationRaw = Number(data.duration_min);

  const base: VideoSession = {
    provider,
    meetingId,
    token,
    isHost,
    role,
    canPublish: typeof data.can_publish === "boolean" ? data.can_publish : true,
    userName: str(data.user_name) ?? str(user?.name),
    counterpartUserId:
      data.counterpart_user_id === null || data.counterpart_user_id === undefined
        ? null
        : Number(data.counterpart_user_id),
    audioOnly: Boolean(data.audio_only),
    durationMin: Number.isFinite(durationRaw) && durationRaw > 0 ? durationRaw : null,
    meetingStartedAt: str(data.meeting_started_at),
    expiresAt: str(data.expires_at),
    raw: data,
  };

  if (provider === "livekit") {
    const serverUrl = str(data.server_url);
    if (!serverUrl) return { ok: false, error: "missing" };
    return {
      ok: true,
      session: {
        ...base,
        serverUrl,
        roomName: str(data.room_name) ?? meetingId,
        identity: str(data.identity),
      },
    };
  }

  // VideoSDK pins participantId inside the token and rejects a join (4002)
  // announced under any other id, so it must reach MeetingProvider's config.
  const claims = decodeJwtPayload(token);
  const participantId =
    str(data.participant_id) ?? str(claims?.participantId) ?? str(user?.id);
  return { ok: true, session: { ...base, participantId } };
}

/** Toast copy for a join response that could not be turned into a session. */
export function describeVideoSessionError(
  error: VideoSessionError,
  { isHost = false }: { isHost?: boolean } = {},
): string {
  const what = isHost ? "The session could not be started" : "This session could not be joined";
  switch (error) {
    case "malformed":
      return `${what} — the video token from the server looks malformed. Please contact support.`;
    case "unsupported":
      return `${what} — the server chose a video service this app does not support yet. Please contact support.`;
    default:
      return `${what} — video credentials are missing from the server response. Please contact support.`;
  }
}

/* ── Shared session clock anchor ─────────────────────────────────────────
 * Both room kinds run one clock model: the host mints { startTs (epoch ms,
 * host clock), durationSec } and everyone counts from the same anchor. How it
 * travels differs per SDK (VideoSDK pubsub vs LiveKit participant attributes);
 * the encoding and the merge rule — earliest startTs wins, the first known
 * duration fills in — are shared here. */

export interface ClockAnchor {
  startTs: number;
  durationSec: number | null;
}

/** "<startTs>[:<durationSec>]" — the attribute / message form. */
export function encodeClockAnchor(anchor: ClockAnchor): string {
  return `${anchor.startTs}${anchor.durationSec ? `:${anchor.durationSec}` : ""}`;
}

export function decodeClockAnchor(value: unknown): ClockAnchor | null {
  const m = /^(\d+)(?::(\d+))?$/.exec(String(value ?? ""));
  if (!m) return null;
  const durationSec = m[2] ? Number(m[2]) : null;
  return { startTs: Number(m[1]), durationSec: durationSec && durationSec > 0 ? durationSec : null };
}

/** Returns `current` ITSELF (same reference) when nothing changes, so callers
 * can skip a re-render. */
export function mergeClockAnchor(
  current: ClockAnchor | null,
  incoming: ClockAnchor | null,
): ClockAnchor | null {
  if (!incoming || !Number.isFinite(Number(incoming.startTs))) return current;
  const incomingDuration = Number(incoming.durationSec);
  const durationSec =
    Number.isFinite(incomingDuration) && incomingDuration > 0 ? Math.round(incomingDuration) : null;
  if (!current || incoming.startTs < current.startTs) {
    return { startTs: Number(incoming.startTs), durationSec: durationSec ?? current?.durationSec ?? null };
  }
  if (current.durationSec == null && durationSec != null) return { ...current, durationSec };
  return current;
}
